import React, { useEffect, useRef, useState } from "react";
import CounterViewModel from "../viewmodels/CounterViewModel";
import useQuote from "../viewmodels/useQuote";
import { useMediaFallback, useMediaReady } from "./mediaFallback";
import styled, { keyframes } from "styled-components";

const MIN_SPINNER_MS = 1500;

const HomeView = () => {
  const [counter, setCounter] = useState(null);
  const [ready, setReady] = useState(false);
  const loadingText = useQuote();
  const [dots, setDots] = useState(".");
  const startedAtRef = useRef(Date.now());

  // The newest artefact — an image or a looping video, whichever format exists.
  const { current, errored, onError } = useMediaFallback(counter);
  // Coming back from the gallery, this artefact is already in the browser cache
  // and its load event can fire before React attaches onLoad — which would leave
  // the spinner up forever. useMediaReady also inspects the element directly.
  const { ready: mediaReady, markReady, mediaRef } = useMediaReady(current && current.url);

  // Animate the dots only while the spinner is up; stop once the media shows.
  useEffect(() => {
    if (ready) return undefined;
    const dotsInterval = setInterval(() => {
      setDots((d) => (d.length === 3 ? "." : d + "."));
    }, 500);
    return () => clearInterval(dotsInterval);
  }, [ready]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const value = await CounterViewModel.getCounter();
        if (!cancelled) setCounter(value);
      } catch (error) {
        console.error("Error fetching the current artefact:", error);
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // If every format failed, stop the spinner instead of hanging forever.
  useEffect(() => {
    if (errored) setReady(true);
  }, [errored]);

  // Reveal the media once it can paint, but keep the quote on screen for a
  // readable minimum.
  useEffect(() => {
    if (!mediaReady) return undefined;
    const elapsed = Date.now() - startedAtRef.current;
    const timer = setTimeout(() => setReady(true), Math.max(0, MIN_SPINNER_MS - elapsed));
    return () => clearTimeout(timer);
  }, [mediaReady]);

  return (
    <Container>
      {!ready && (
        <LoadingContainer>
          <LoadingText>
            {loadingText}
            {dots}
          </LoadingText>
          <LoadingSpinner />
        </LoadingContainer>
      )}
      {current &&
        (current.type === "video" ? (
          <Image
            as="video"
            ref={mediaRef}
            className={ready ? "visible" : ""}
            src={current.url}
            autoPlay
            loop
            muted
            playsInline
            disablePictureInPicture
            onLoadedData={markReady}
            onError={onError}
          />
        ) : (
          <Image
            ref={mediaRef}
            className={ready ? "visible" : ""}
            src={current.url}
            alt="Current artefact"
            onLoad={markReady}
            onError={onError}
          />
        ))}
    </Container>
  );
};

export default HomeView;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Container = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: calc(100vh - var(--nav-h));
  padding: 20px;
  box-sizing: border-box;
`;

// Absolute overlay so the (invisible-while-loading) image can never push the
// spinner off-centre — it stays centred in the viewport regardless.
const LoadingContainer = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const LoadingSpinner = styled.div`
  border: 6px solid #000;
  border-top: 6px solid var(--accent);
  border-radius: 50%;
  width: 56px;
  height: 56px;
  animation: ${spin} 1s linear infinite;
  margin-top: 22px;
`;

const LoadingText = styled.div`
  color: var(--accent);
  width: 100%;
  text-align: center;
  margin-bottom: 6px;
  font-size: clamp(16px, 2.2vw, 24px);
  padding: 0 16px;
  box-sizing: border-box;
  white-space: normal;
  max-width: 40ch;
  margin-left: auto;
  margin-right: auto;
`;

const Image = styled.img`
  max-width: min(92%, 1100px);
  max-height: 88vh;
  object-fit: contain;
  border-radius: var(--radius-sm);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.35);
  opacity: 0;

  &.visible {
    animation: ${fadeIn} 1.4s var(--ease) forwards;
  }
`;
